/* ============================================================================
   Juice — the app-wide feedback bus.

   Any component can fire `juice.burst(...)` / `juice.shake()` / `juice.flash()`
   without threading refs or callbacks through the tree. A single <JuiceLayer />
   mounted at the root subscribes and does the rendering.

   Deliberately not React state: these are fire-and-forget visual events at
   input rate, and routing them through context would re-render the game on
   every particle.
   ========================================================================== */

const listeners = new Set();

function emit(event) {
  for (const listener of listeners) listener(event);
}

export function onJuice(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

let seq = 0;
const nextId = () => {
  seq += 1;
  return seq;
};

export const PALETTES = {
  fire: ['#ff1f3d', '#ff4d5f', '#ff7a29', '#ffd23f'],
  cyan: ['#2ee9ff', '#8b5cff', '#f4f8ff'],
  gold: ['#ffd23f', '#ff7a29', '#f4f8ff', '#ff1f3d'],
  win: ['#ff1f3d', '#ffd23f', '#2ee9ff', '#7dff6b', '#ff3ad6', '#f4f8ff'],
};

export const juice = {
  /**
   * Spray particles outward from a viewport point.
   * @param {object} opts
   * @param {number} opts.x       viewport px
   * @param {number} opts.y       viewport px
   * @param {number} [opts.count] number of particles
   * @param {string} [opts.palette] key of PALETTES
   * @param {number} [opts.power] travel distance multiplier
   * @param {number} [opts.spread] cone angle in degrees; 360 = full circle
   * @param {number} [opts.angle] cone centre in degrees; 0 = straight up
   */
  burst({ x, y, count = 18, palette = 'fire', power = 1, spread = 360, angle = 0 }) {
    emit({ kind: 'burst', id: nextId(), x, y, count, palette, power, spread, angle });
  },

  /** Confetti rain from the top of the viewport. */
  rain({ count = 60, palette = 'win' } = {}) {
    emit({ kind: 'rain', id: nextId(), count, palette });
  },

  /** Knock the whole stage. strength: 'light' | 'medium' | 'heavy' */
  shake(strength = 'medium') {
    emit({ kind: 'shake', id: nextId(), strength });
  },

  /** Full-screen color wash that fades out. */
  flash(color = 'rgba(255, 31, 61, 0.5)', duration = 320) {
    emit({ kind: 'flash', id: nextId(), color, duration });
  },

  /** Floating text that rises and fades — "+1", "MISS", and so on. */
  float({ text, x, y, tone = 'gold' }) {
    emit({ kind: 'float', id: nextId(), text, x, y, tone });
  },
};

export default juice;
